/**
 * The notification renderer registry (docs/NOTIFICATIONS.md "The type registry").
 *
 * The DB stores an English `title` / `message` for every row; the web renders
 * i18n text from `type + payload` instead, and falls back to the stored
 * columns when the `type` has no renderer OR the payload is incomplete for
 * its renderer — that keeps a legacy row, a row from a newer API than the
 * deployed web, or a half-formed payload readable instead of blank.
 *
 * Adding a type = a row in the docs table + an entry here + the emitter.
 */
import type { TFunction } from "i18next";
import { Bell, CalendarClock, ClipboardList, CreditCard, MessageSquare, type LucideIcon } from "lucide-react";
import type { ChannelMentionPayload, NotificationWire } from "@releve/shared";

export interface RenderedNotification {
  icon: LucideIcon;
  title: string;
  message: string | null;
}

/** Resolves a user id to a current display name (`useMemberDirectory().nameOf`). */
export type NameResolver = (userId: string | null | undefined) => string | null;

/** Returns `null` when the payload is too incomplete to render — the caller falls back. */
export type NotificationRenderer = (
  n: NotificationWire,
  t: TFunction,
  nameOf: NameResolver,
) => RenderedNotification | null;

/**
 * `interaction.follow_up_due` payload, stamped by
 * `interactions_notify_due_followups()` (20260928170000_interactions.sql).
 * The link (`/resources/people/<person_code>/interactions`) is on the row.
 */
export interface InteractionFollowUpDuePayload {
  interaction_id: string;
  interaction_code: string;
  participant_id: string;
  person_id: string;
  person_code: string;
}

/**
 * `survey.responses_digest` payload, stamped by the daily
 * `surveys_notify_response_digest()` job (supabase/migrations/*_surveys.sql):
 * the number of PUBLIC responses since the last digest. The link
 * (`/resources/people/surveys/<code>/results`) is on the row. Name-free:
 * responses are anonymous.
 */
export interface SurveyResponsesDigestPayload {
  survey_id: string;
  survey_code: string;
  count: number;
}

/**
 * `subscription.payment_failed` payload, stamped by
 * `subscription_notify_payment_failed()` when the billing webhook's sync moves
 * an org's subscription INTO `past_due` (20261030120000_subscriptions.sql).
 * Sent to the org's admins; the link (`/settings/subscription`) is on the row.
 */
export interface SubscriptionPaymentFailedPayload {
  plan_code: string;
  status: string;
}

export const NOTIFICATION_RENDERERS: Record<string, NotificationRenderer> = {
  "channel.mention": (n, t, nameOf) => {
    const payload = n.payload as Partial<ChannelMentionPayload>;
    if (!payload.comment_id) return null;
    // The author's CURRENT display name wins; the name stamped at emit time
    // covers a member who has since left; never an email (GDPR_COMPLIANCE.md).
    const author = nameOf(payload.author_id) ?? payload.author_name ?? t("notifications.someone");
    const excerpt = payload.excerpt?.trim();
    const context = t("notifications.types.channel_mention.message", {
      context: payload.context_name ?? "",
    });
    return {
      icon: MessageSquare,
      title: t("notifications.types.channel_mention.title", { author }),
      message: excerpt ? `${context} — “${excerpt}”` : context,
    };
  },
  "interaction.follow_up_due": (n, t) => {
    const payload = n.payload as Partial<InteractionFollowUpDuePayload>;
    if (!payload.interaction_code) return null;
    return {
      icon: CalendarClock,
      title: t("notifications.types.interaction_follow_up_due.title"),
      message: t("notifications.types.interaction_follow_up_due.message", { code: payload.interaction_code }),
    };
  },
  "survey.responses_digest": (n, t) => {
    const payload = n.payload as Partial<SurveyResponsesDigestPayload>;
    if (!payload.survey_code || typeof payload.count !== "number") return null;
    return {
      icon: ClipboardList,
      title: t("notifications.types.survey_responses_digest.title", { code: payload.survey_code }),
      message: t("notifications.types.survey_responses_digest.message", {
        count: payload.count,
        code: payload.survey_code,
      }),
    };
  },
  "subscription.payment_failed": (n, t) => {
    const payload = n.payload as Partial<SubscriptionPaymentFailedPayload>;
    if (!payload.plan_code) return null;
    return {
      icon: CreditCard,
      title: t("notifications.types.subscription_payment_failed.title"),
      message: t("notifications.types.subscription_payment_failed.message"),
    };
  },
};

/** Registry hit, else the stored English columns under the generic bell icon. */
export function renderNotification(
  n: NotificationWire,
  t: TFunction,
  nameOf: NameResolver,
): RenderedNotification {
  const rendered = NOTIFICATION_RENDERERS[n.type]?.(n, t, nameOf) ?? null;
  return rendered ?? { icon: Bell, title: n.title, message: n.message };
}
